import NavDrawer from './NavDrawer';
import { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabaseClient';
import { SET_REGISTRY } from './setRegistry';
import { localImageUrl, CONDITION_SLOTS } from './CardImage';
import MarketplaceListingCard from './components/MarketplaceListingCard';
import ListingDetailModal from './components/ListingDetailModal';
import { PAR_MAP, parseCardKey, parallelFor, baseCardKey, loadCardNamesForSets } from './lib/cardKey';
import { lightAccent } from './theme';

// ───────────────────────────────────────────────────────────────────
// Marketplace 2.0 adapter — maps a collections row + profile + trust
// stats to the demo-shape `listing` object the new components consume.
//
// Phase 2E.1 Step 6: data sources fully live now.
//   - verified    → profiles.identity_verified         (Phase 2E.1 Step 1)
//   - paypal/venmo → profiles.accepts_* + handles      (Phase 2E.1 Step 2a)
//   - sellerCount/Color, buyerCount/Color → user_trust_stats (Phase 2D)
//     (absent view row = sprout state, per the spec)
// ───────────────────────────────────────────────────────────────────
function toListingShape(item, profileMap, cardNameLookup, statsMap) {
  const { setId, cardN, parallelId, serialNum } = parseCardKey(item.card_key);
  const setMeta = SET_REGISTRY.find(s => s.id === setId);
  const par = parallelId
    ? (PAR_MAP[parallelId] || { name: parallelId, color: 'var(--text)', print_run: null })
    : null;
  const profile = profileMap[item.user_id];

  const baseKey = item.card_key.split('__').slice(0, 3).join('__');
  const cardName = (cardNameLookup && cardNameLookup[baseKey]) || '';

  const isSale = item.status === 'for_sale';
  const price = isSale && item.sale_price ? Number(item.sale_price) : null;

  // Grade composition mirrors existing Marketplace.js grade display logic.
  let grade = null;
  if (item.grading_company && item.grading_grade) {
    grade = `${item.grading_company} ${item.grading_grade}`;
    if (item.grading_tag_score) grade += ` (TAG ${item.grading_tag_score})`;
  } else if (item.grading_tag_score) {
    grade = `TAG ${item.grading_tag_score}`;
  }

  // Trust stats — sprout when the user_trust_stats view has no row for that
  // (user_id, role) pair. Decision 4 spec: row missing = sprout state.
  const userStats = (statsMap && statsMap[item.user_id]) || {};
  const sellerStats = userStats.seller || null;
  const buyerStats = userStats.buyer || null;
  const sellerCount = sellerStats?.count || 0;
  const sellerColor = sellerStats?.color || 'sprout';
  const buyerCount = buyerStats?.count || 0;
  const buyerColor = buyerStats?.color || 'sprout';

  return {
    id: `${item.card_key}__${item.user_id}`,
    cardKey: item.card_key,
    set: setMeta?.shortName || setId,
    setName: setMeta?.name || setMeta?.shortName || setId,
    cardNumber: cardN,
    serial: serialNum,
    name: cardName,
    grade,
    price,
    estValue: item.trade_value != null ? Number(item.trade_value) : null,
    status: isSale ? 'FOR SALE' : 'FOR TRADE',
    statusType: isSale ? 'sale' : 'trade',
    parallel: par?.name || null,
    parallelNum: par?.print_run || null,
    escrow: typeof price === 'number' && price >= 500,
    imageUrl: localImageUrl(item.user_id, item.card_key, 'front'),
    imageBackUrl: localImageUrl(item.user_id, item.card_key, 'back'),
    // Candidate condition close-ups. URLs are always generated; the buyer gallery
    // probes each and shows only the ones that actually exist in storage.
    conditionPhotos: CONDITION_SLOTS.map(({ slot, label }) => ({
      slot,
      label,
      url: localImageUrl(item.user_id, item.card_key, slot),
    })),
    // Real payment methods — silent absence is the design (Decision 2 Addendum).
    // public_profiles computes these as (toggle AND handle present); the raw
    // handle/email never leaves the database for other users.
    acceptsPaypal: !!profile?.accepts_paypal,
    acceptsVenmo:  !!profile?.accepts_venmo,
    seller: {
      id: item.user_id,
      handle: '@' + (profile?.username || '?'),
      verified: !!profile?.identity_verified,
      sellerCount,
      sellerColor,
      sellerStats: null, // future: pass through breakdown for the StatCells in TrustHistoryView
      buyerCount,
      buyerColor,
    },
  };
}

// ─── Probing card photo (Sold feed + Wanted board) ─────────────────────
// Read-only tile photo. Tries the real card photo for (ownerId, cardKey);
// falls back to a branded gradient placeholder when there's no photo.
function CardPhoto({ sellerId, cardKey, setName, cardN, parallelName, parColor }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!sellerId || !cardKey) return;
    const u = localImageUrl(sellerId, cardKey, 'front');
    const img = new Image();
    img.onload = () => setUrl(u);
    img.onerror = () => setUrl(null);
    img.src = u;
  }, [sellerId, cardKey]);
  const box = { width: '100%', aspectRatio: '0.72', borderRadius: '3px', overflow: 'hidden', marginBottom: '10px' };
  if (url) {
    return <div style={{ ...box, backgroundColor: 'var(--bg-2)' }}><img src={url} alt="card" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /></div>;
  }
  return (
    <div style={{ ...box, backgroundColor: 'var(--bg-2)', backgroundImage: 'linear-gradient(135deg,var(--chip-purple) 0%,var(--panel-2) 45%,var(--chip-teal) 70%,var(--chip-purple) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)' }}>
      {setName && <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--muted-5)', marginBottom: '8px', padding: '0 8px', textAlign: 'center' }}>{setName}</div>}
      <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '26px', fontWeight: 900, color: 'var(--text-strong)' }}>#{cardN}</div>
      {parallelName && <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', color: parColor || 'var(--purple)', marginTop: '6px' }}>{parallelName}</div>}
    </div>
  );
}

function SoldCard({ row, cardNameLookup }) {
  const { setId, cardN, parallelId, serialNum } = parseCardKey(row.cardKey);
  const setMeta = SET_REGISTRY.find(s => s.id === setId);
  const par = parallelFor(parallelId);
  const cardName = cardNameLookup?.[baseCardKey(row.cardKey)];
  return (
    <div style={M.card}>
      <CardPhoto sellerId={row.sellerId} cardKey={row.cardKey} setName={setMeta?.shortName} cardN={cardN} parallelName={par?.name} parColor={par?.color} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <span style={{ ...M.statusBadge, borderColor: 'rgba(var(--green-rgb),0.5)', color: 'var(--green)', background: 'rgba(var(--green-rgb),0.1)' }}>✓ SOLD</span>
        {typeof row.price === 'number' && <span style={M.price}>${row.price.toFixed(2)}</span>}
      </div>
      {setMeta && <div style={M.setName}>{setMeta.shortName}</div>}
      <div style={M.cardNum}>Card #{cardN}{cardName ? ` · ${cardName}` : ''}{serialNum ? ` · Serial #${serialNum}` : ''}</div>
      {par && (
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '8px', padding: '2px 6px', border: '1px solid', borderRadius: '1px', borderColor: lightAccent(par.color) + '66', color: lightAccent(par.color), background: lightAccent(par.color) + '12' }}>
            {par.name}{par.print_run ? ` /${par.print_run}` : ''}
          </span>
        </div>
      )}
      <div style={M.divider} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        <span style={M.seller}>{row.seller}</span>
        <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', color: 'var(--muted-2)' }}>SOLD · {row.soldDate}</span>
      </div>
    </div>
  );
}

// ─── Wanted board ───────────────────────────────────────────────────
// Wishlist cards from public profiles, surfaced so other collectors can
// offer a match. The wisher doesn't own the card, so the photo borrows a
// public specimen of the same card_key (photoOwnerId) when one exists;
// CardPhoto falls back to the branded gradient placeholder otherwise.
function WantedCard({ row, profile, photoOwnerId, cardNameLookup, isOwn, busy, onContact }) {
  const { setId, cardN, parallelId } = parseCardKey(row.card_key);
  const setMeta = SET_REGISTRY.find(s => s.id === setId);
  const par = parallelFor(parallelId);
  const cardName = cardNameLookup?.[baseCardKey(row.card_key)];
  return (
    <div style={M.card}>
      <CardPhoto sellerId={photoOwnerId} cardKey={row.card_key} setName={setMeta?.shortName} cardN={cardN} parallelName={par?.name} parColor={par?.color} />
      <div style={{ marginBottom: '8px' }}>
        <span style={{ ...M.statusBadge, borderColor: 'rgba(var(--gold-rgb),0.5)', color: 'var(--gold)', background: 'rgba(var(--gold-rgb),0.1)' }}>★ WANTED</span>
      </div>
      {setMeta && <div style={M.setName}>{setMeta.shortName}</div>}
      <div style={M.cardNum}>Card #{cardN}{cardName ? ` · ${cardName}` : ''}</div>
      {par && (
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '8px', padding: '2px 6px', border: '1px solid', borderRadius: '1px', borderColor: lightAccent(par.color) + '66', color: lightAccent(par.color), background: lightAccent(par.color) + '12' }}>
            {par.name}{par.print_run ? ` /${par.print_run}` : ''}
          </span>
        </div>
      )}
      {row.willing_to_pay != null && (
        <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', color: 'var(--brand-gold)', marginBottom: '8px' }}>
          Willing to pay <span style={{ fontFamily: "'Orbitron',monospace", fontWeight: 700, fontSize: '13px' }}>${Number(row.willing_to_pay).toFixed(2)}</span> · Mint/Gem Mint
        </div>
      )}
      <div style={{ flex: 1 }} />
      <div style={M.divider} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        <span style={M.seller}>@{profile?.username || '?'}{profile?.identity_verified ? ' ✓' : ''}</span>
        {isOwn ? (
          <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', color: 'var(--muted-2)' }}>YOUR WISHLIST</span>
        ) : (
          <button onClick={onContact} disabled={busy} style={{ ...M.contactBtn, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'OPENING…' : 'I HAVE THIS →'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Marketplace({ user }) {
  const [listings, setListings] = useState([]);
  const [profileMap, setProfileMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSet, setFilterSet] = useState('all');
  const [filterParallel, setFilterParallel] = useState('all');
  const [filterCard, setFilterCard] = useState('all');
  const [filterSerial, setFilterSerial] = useState('all');
  const [modalItem, setModalItem] = useState(null);
  // Phase 2E.1 Step 6: user_trust_stats keyed by user_id; nested by role.
  //   statsMap[uid] = { seller?: { count, color }, buyer?: { count, color } }
  // Absent role entry = sprout for that role (per Decision 4 / Phase 2D notes).
  const [statsMap, setStatsMap] = useState({});
  const [toast, setToast] = useState(null);

  // Sold feed (Marketplace 2.1, Gap #9). Lazy-loaded the first time the user
  // switches to the SOLD view. Sourced from the marketplace-sold-feed edge fn.
  const [view, setView] = useState('live'); // 'live' | 'sold' | 'wanted'
  const [soldRows, setSoldRows] = useState([]);
  const [soldLoading, setSoldLoading] = useState(false);
  const [soldLoaded, setSoldLoaded] = useState(false);
  const [soldError, setSoldError] = useState(false);

  // Wanted board — wishlist cards from public profiles, lazy-loaded the first
  // time the user switches to the WANTED view. Wishlist rows of public
  // profiles are already publicly readable (same RLS path as the public
  // profile page), so this is a plain collections read.
  const [wantedRows, setWantedRows] = useState([]);
  // card_key -> user_id of any public copy with photos, for the tile image.
  const [wantedPhotoOwners, setWantedPhotoOwners] = useState({});
  const [wantedLoading, setWantedLoading] = useState(false);
  const [wantedLoaded, setWantedLoaded] = useState(false);
  const [contactBusy, setContactBusy] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const nav = path => { window.history.pushState({}, '', path); window.dispatchEvent(new PopStateEvent('popstate')); };

  useEffect(() => {
    (async () => {
      // Profiles — extended with Phase 2E.1 columns (verified flag + payment methods).
      const { data: profiles } = await supabase.from('public_profiles')
        .select('id,username,identity_verified,accepts_paypal,accepts_venmo')
        .eq('is_public', true);

      // Trust stats from the user_trust_stats view (Phase 2D). One row per
      // (user_id, role); absence of a (user_id, role) row = sprout state.
      const { data: statsRows } = await supabase
        .from('user_trust_stats')
        .select('user_id, role, total_transactions, badge_color');
      const sMap = {};
      (statsRows || []).forEach(s => {
        if (!sMap[s.user_id]) sMap[s.user_id] = {};
        sMap[s.user_id][s.role] = { count: s.total_transactions, color: s.badge_color };
      });
      setStatsMap(sMap);

      if (!profiles?.length) { setLoading(false); return; }
      const pMap = {};
      profiles.forEach(p => { pMap[p.id] = p; });
      setProfileMap(pMap);
      const { data: rows } = await supabase.from('collections')
        .select('card_key,user_id,status,sale_price,card_condition,trade_value,trade_notes,grading_company,grading_grade,grading_cert_num,grading_serial_num,grading_print_run,grading_tag_score,grading_report_url,grading_pop_this,grading_pop_higher,is_paused')
        .in('user_id', profiles.map(p => p.id))
        .in('status', ['for_sale', 'trade'])
        .eq('marketplace_eligible', true)
        // Phase 2E.1 § 5.6 Decision 6: hide $500+ drafts awaiting verification.
        // .or() handles both NULL (legacy rows pre-2E.1) and false uniformly.
        .or('is_paused.is.null,is_paused.eq.false')
        // Phase 2F: durable sold-state. sold_at is set at payment-commit by the
        // sale writers and is NOT touched by any unpause path, so a sold card can
        // never be resurrected onto the grid the way the overloaded is_paused was.
        .is('sold_at', null);

      if (!rows?.length) { setLoading(false); return; }
      // Base-vs-serial dedupe: if a base record (parallel or card) AND one of its
      // serial variants are both listed, hide the base — the serial is the specific
      // item being sold, so showing both is a duplicate of the same physical card.
      // Read-time only; eligibility stays strictly per-record (Option A).
      const deduped = rows.filter(r =>
        r.card_key.includes('__s__') ||
        !rows.some(o => o.user_id === r.user_id && o.card_key.startsWith(r.card_key + '__s__'))
      );
      setListings(deduped);
      setLoading(false);
    })();
  }, []);

  // Enrich listings with parsed info
  const enriched = useMemo(() => listings.map(item => {
    const { setId, cardN, parallelId } = parseCardKey(item.card_key);
    return { ...item, setId, cardN, parallelId };
  }), [listings]);

  // Card name lookup: card_key (base form) -> card name
  // Built lazily by dynamic-importing each unique set's data file.
  const [cardNameLookup, setCardNameLookup] = useState({});
  useEffect(() => {
    const setIds = [...new Set(enriched.map(i => i.setId))];
    loadCardNamesForSets(setIds).then(map => {
      setCardNameLookup(prev => ({ ...prev, ...map }));
    });
  }, [enriched]);

  // Sold feed — lazy fetch on first switch to the SOLD view.
  useEffect(() => {
    if (view !== 'sold' || soldLoaded || soldLoading) return;
    setSoldLoading(true);
    setSoldError(false);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('marketplace-sold-feed', { body: { limit: 100 } });
        if (error) throw error;
        const rows = (data && data.rows) || [];
        setSoldRows(rows);
        const setIds = [...new Set(rows.map(r => parseCardKey(r.cardKey).setId))];
        const map = await loadCardNamesForSets(setIds);
        setCardNameLookup(prev => ({ ...prev, ...map }));
      } catch (e) {
        console.error('[sold-feed] fetch failed:', e?.message || e);
        setSoldError(true);
      } finally {
        setSoldLoading(false);
        setSoldLoaded(true);
      }
    })();
  }, [view, soldLoaded, soldLoading]);

  // Wanted board — lazy fetch on first switch to the WANTED view.
  useEffect(() => {
    if (view !== 'wanted' || wantedLoaded || wantedLoading) return;
    const ids = Object.keys(profileMap);
    if (!ids.length) { setWantedLoaded(true); return; }
    setWantedLoading(true);
    (async () => {
      try {
        const { data: rows } = await supabase.from('collections')
          .select('card_key,user_id,status,willing_to_pay')
          .in('user_id', ids)
          .eq('status', 'wishlist');
        const list = (rows || []).slice()
          .sort((a, b) => a.card_key.localeCompare(b.card_key, undefined, { numeric: true }));
        setWantedRows(list);
        const setIds = [...new Set(list.map(r => parseCardKey(r.card_key).setId))];
        const map = await loadCardNamesForSets(setIds);
        setCardNameLookup(prev => ({ ...prev, ...map }));
        // Tile images: the wisher doesn't own the card, so borrow a photo from
        // any public copy of the same card_key (RLS already scopes this read
        // to public profiles). Chunked .in() keeps the URL within limits.
        const keys = [...new Set(list.map(r => r.card_key))];
        const owners = {};
        for (let i = 0; i < keys.length; i += 50) {
          const { data: photoRows } = await supabase.from('collections')
            .select('card_key,user_id')
            .in('card_key', keys.slice(i, i + 50))
            .eq('has_photos', true);
          (photoRows || []).forEach(r => { if (!owners[r.card_key]) owners[r.card_key] = r.user_id; });
        }
        setWantedPhotoOwners(owners);
      } catch (e) {
        console.error('[wanted] fetch failed:', e?.message || e);
      } finally {
        setWantedLoading(false);
        setWantedLoaded(true);
      }
    })();
  }, [view, wantedLoaded, wantedLoading, profileMap]);

  // Wanted rows enriched with parsed info, mirroring the live grid's shape.
  const enrichedWanted = useMemo(() => wantedRows.map(item => {
    const { setId, cardN, parallelId } = parseCardKey(item.card_key);
    return { ...item, setId, cardN, parallelId };
  }), [wantedRows]);

  // Cascading filter options — computed from whichever grid is on screen
  // (live listings or the wanted board), so the set/parallel/card selects
  // work in both views.
  const filterBase = view === 'wanted' ? enrichedWanted : enriched;

  const availableSets = useMemo(() => {
    const ids = new Set(filterBase.map(i => i.setId));
    return [...ids].map(id => SET_REGISTRY.find(s => s.id === id)).filter(Boolean).sort((a, b) => a.shortName.localeCompare(b.shortName));
  }, [filterBase]);

  const availableParallels = useMemo(() => {
    const base = filterSet === 'all' ? filterBase : filterBase.filter(i => i.setId === filterSet);
    const ids = new Set(base.filter(i => i.parallelId).map(i => i.parallelId));
    return [...ids].map(id => PAR_MAP[id] || { id, name: id, print_run: null, color: 'var(--text)' })
      .sort((a, b) => (a.print_run || 9999) - (b.print_run || 9999));
  }, [filterBase, filterSet]);

  const availableCards = useMemo(() => {
    let base = filterSet === 'all' ? filterBase : filterBase.filter(i => i.setId === filterSet);
    if (filterParallel !== 'all') base = base.filter(i => i.parallelId === filterParallel);
    const nums = [...new Set(base.map(i => i.cardN))].sort((a, b) => parseInt(a) - parseInt(b));
    return nums;
  }, [filterBase, filterSet, filterParallel]);

  const availableSerials = useMemo(() => {
    let base = filterSet === 'all' ? filterBase : filterBase.filter(i => i.setId === filterSet);
    if (filterParallel !== 'all') base = base.filter(i => i.parallelId === filterParallel);
    if (filterCard !== 'all') base = base.filter(i => i.cardN === filterCard);
    const serials = [...new Set(base.filter(i => i.serialNum).map(i => i.serialNum))]
      .sort((a, b) => parseInt(a) - parseInt(b));
    return serials;
  }, [filterBase, filterSet, filterParallel, filterCard]);

  // Apply filters
  const filtered = useMemo(() => enriched.filter(item => {
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    if (filterSet !== 'all' && item.setId !== filterSet) return false;
    if (filterParallel !== 'all' && item.parallelId !== filterParallel) return false;
    if (filterCard !== 'all' && item.cardN !== filterCard) return false;
    if (filterSerial !== 'all' && item.serialNum !== filterSerial) return false;
    return true;
  }), [enriched, filterStatus, filterSet, filterParallel, filterCard]);

  // Same cascade for the wanted board (no status dimension — all rows are
  // wishlist by construction).
  const filteredWanted = useMemo(() => enrichedWanted.filter(item => {
    if (filterSet !== 'all' && item.setId !== filterSet) return false;
    if (filterParallel !== 'all' && item.parallelId !== filterParallel) return false;
    if (filterCard !== 'all' && item.cardN !== filterCard) return false;
    return true;
  }), [enrichedWanted, filterSet, filterParallel, filterCard]);

  const handleSetFilter = v => { setFilterSet(v); setFilterParallel('all'); setFilterCard('all'); };
  const handleParallelFilter = v => { setFilterParallel(v); setFilterCard('all'); setFilterSerial('all'); };
  const handleCardFilter = v => { setFilterCard(v); setFilterSerial('all'); };
  // The status dropdown drives the LIVE/SOLD/WANTED view. 'sold' and 'wanted'
  // are separate data paths (transactions / wishlist rows, not the live
  // listings grid), so they switch the view rather than filtering the live list.
  const handleStatusChange = v => {
    if (v === 'sold') setView('sold');
    else if (v === 'wanted') setView('wanted');
    else { setView('live'); setFilterStatus(v); }
  };

  // ─── Wanted board: open (or create) a thread with the wishlist owner ──
  // Conversations RLS allows a participant to insert their own conversation,
  // and Conversation.js already renders a plain chat when no transaction is
  // attached — so this needs no edge function. participant_1 = wishlist owner:
  // they're the potential buyer, matching the payment flows where
  // participant_1 is always the buyer.
  const handleContactWanted = async (row) => {
    if (!user?.id || row.user_id === user.id || contactBusy) return;
    const busyKey = row.card_key + row.user_id;
    setContactBusy(busyKey);
    try {
      // Reuse an existing thread for this pair + card before creating a new one.
      const { data: existing } = await supabase.from('conversations')
        .select('id')
        .eq('card_key', row.card_key)
        .or(`and(participant_1.eq.${row.user_id},participant_2.eq.${user.id}),and(participant_1.eq.${user.id},participant_2.eq.${row.user_id})`)
        .order('created_at', { ascending: false })
        .limit(1);
      if (existing?.length) { nav('/messages/' + existing[0].id); return; }

      const { setId, cardN, parallelId } = parseCardKey(row.card_key);
      const setMeta = SET_REGISTRY.find(s => s.id === setId);
      const par = parallelId ? (PAR_MAP[parallelId] || { name: parallelId }) : null;
      const { data: conv, error } = await supabase.from('conversations').insert({
        participant_1: row.user_id,
        participant_2: user.id,
        card_key: row.card_key,
        card_snapshot: {
          setName: setMeta?.name || setMeta?.shortName || setId,
          cardN,
          parallelName: par?.name || null,
          // Buyer's stated Mint/Gem-Mint offer — surfaces in the RE: banner.
          price: row.willing_to_pay != null ? Number(row.willing_to_pay) : null,
          status: 'wanted',
        },
      }).select('id').single();
      if (error || !conv?.id) throw new Error(error?.message || 'conversation insert failed');

      // System seed so the wishlist owner sees why this thread exists even
      // before the first real message lands.
      const myHandle = profileMap[user.id]?.username ? '@' + profileMap[user.id].username : 'A collector';
      await supabase.from('messages').insert({
        conversation_id: conv.id,
        sender_id: null,
        body: `${myHandle} is reaching out about a card on your wishlist.`,
      });
      nav('/messages/' + conv.id);
    } catch (e) {
      console.error('[wanted] contact failed:', e?.message || e);
      showToast("Couldn't start the conversation. Please try again.");
    } finally {
      setContactBusy(null);
    }
  };

  if (loading) return <div style={M.screen}><span style={M.screenTxt}>LOADING MARKETPLACE...</span></div>;

  return (
    <div style={M.page}>
      <NavDrawer />
      <div style={M.stars} />

      <header style={M.header}>
        <div style={M.hInner}>
          <div>
            <div style={M.logoLine}>COLLECTIONKEEPER · COMMUNITY</div>
            <h1 style={M.title}><span style={{ color: 'var(--purple)' }}>MARKET</span><span style={{ color: 'var(--cyan)' }}>PLACE</span></h1>
            <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '12px', letterSpacing: '3px', color: 'var(--brand-gold)', marginTop: '4px' }}>NO FEES. EVER.</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}></div>
        </div>
        <div style={{ height: '1px', background: 'linear-gradient(90deg,transparent,var(--purple),var(--cyan),var(--purple),transparent)' }} />
      </header>

      {view === 'live' && (<>
      {/* Stats */}
      <div style={M.statsBar}>
        <span style={{ ...M.pill, borderColor: 'var(--purple)', color: 'var(--purple)' }}>{filtered.filter(i => i.status === 'for_sale').length} FOR SALE</span>
        <span style={{ ...M.pill, borderColor: 'var(--cyan)', color: 'var(--cyan)' }}>{filtered.filter(i => i.status === 'trade').length} FOR TRADE</span>
        <span style={{ ...M.pill, borderColor: 'var(--muted-2)', color: 'var(--muted-2)' }}>{filtered.length} TOTAL LISTING{filtered.length !== 1 ? 'S' : ''}</span>
      </div>

      {/* Disclaimer */}
      <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-2)', textAlign: 'center', padding: '10px 24px', letterSpacing: '0.5px', lineHeight: 1.6, borderBottom: '1px solid var(--panel-3)' }}>
        Only cards from public profiles with front + back photos marked For Sale or For Trade appear here.
      </div>
      </>)}

      {/* Filters — always visible; the status select switches LIVE / SOLD */}
      <div style={M.filterBar}>
        <span style={M.filterLabel}>FILTER</span>

        <select value={view === 'live' ? filterStatus : view} onChange={e => handleStatusChange(e.target.value)} style={M.select}>
          <option value="all">ALL STATUS</option>
          <option value="for_sale">FOR SALE</option>
          <option value="trade">FOR TRADE</option>
          <option value="sold">SOLD</option>
          <option value="wanted">WANTED</option>
        </select>

        {view !== 'sold' && (<>
        <select value={filterSet} onChange={e => handleSetFilter(e.target.value)} style={M.select}>
          <option value="all">ALL SETS</option>
          {availableSets.map(s => <option key={s.id} value={s.id}>{s.shortName}</option>)}
        </select>

        {filterSet !== 'all' && (
          <select value={filterParallel} onChange={e => handleParallelFilter(e.target.value)} style={M.select}>
            <option value="all">ALL PARALLELS</option>
            {availableParallels.map(p => (
              <option key={p.id} value={p.id}>{p.name}{p.print_run ? ` /${p.print_run}` : ''}</option>
            ))}
          </select>
        )}

        {filterSet !== 'all' && availableCards.length > 0 && (
          <select value={filterCard} onChange={e => handleCardFilter(e.target.value)} style={M.select}>
            <option value="all">ALL CARD #S</option>
            {availableCards.map(n => <option key={n} value={n}>Card #{n}</option>)}
          </select>
        )}

        {filterSet !== 'all' && availableSerials.length > 0 && (
          <select value={filterSerial} onChange={e => setFilterSerial(e.target.value)} style={M.select}>
            <option value="all">ALL SERIALS</option>
            {availableSerials.map(n => <option key={n} value={n}>#{n}</option>)}
          </select>
        )}

        {(filterStatus !== 'all' || filterSet !== 'all' || filterParallel !== 'all' || filterCard !== 'all' || filterSerial !== 'all') && (
          <button onClick={() => { setFilterStatus('all'); setFilterSet('all'); setFilterParallel('all'); setFilterCard('all'); setFilterSerial('all'); }} style={{ ...M.btn, borderColor: 'rgba(var(--brand-gold-rgb),0.4)', color: 'var(--brand-gold)', fontSize: '8px' }}>
            CLEAR ✕
          </button>
        )}
        </>)}
      </div>

      {/* Live grid */}
      {view === 'live' && (
      <main style={M.main}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '4px', color: 'var(--muted-2)', marginBottom: '8px' }}>NO LISTINGS FOUND</div>
            <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--border-2)' }}>Try adjusting your filters</div>
          </div>
        ) : (
          <div style={M.grid}>
            {filtered.map((item, i) => (
              <MarketplaceListingCard
                key={item.card_key + item.user_id + i}
                listing={toListingShape(item, profileMap, cardNameLookup, statsMap)}
                onOpen={() => setModalItem(item)}
              />
            ))}
          </div>
        )}
      </main>
      )}

      {view === 'wanted' && (
        <>
          <div style={M.statsBar}>
            <span style={{ ...M.pill, borderColor: 'var(--gold)', color: 'var(--gold)' }}>{filteredWanted.length} WANTED</span>
            <span style={{ ...M.pill, borderColor: 'var(--muted-2)', color: 'var(--muted-2)' }}>{new Set(filteredWanted.map(r => r.user_id)).size} COLLECTOR{new Set(filteredWanted.map(r => r.user_id)).size !== 1 ? 'S' : ''}</span>
          </div>
          <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-2)', textAlign: 'center', padding: '12px 24px', letterSpacing: '0.5px', lineHeight: 1.6, borderBottom: '1px solid var(--panel-3)' }}>
            Cards collectors are hunting for. Have one? Message them and make a deal.
          </div>
          <main style={M.main}>
            {(wantedLoading || !wantedLoaded) ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '4px', color: 'var(--muted-2)' }}>LOADING WANTED…</div>
              </div>
            ) : wantedRows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '4px', color: 'var(--muted-2)', marginBottom: '8px' }}>NO WANTED CARDS YET</div>
                <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--border-2)' }}>Cards marked Wishlist on public profiles will show up here.</div>
              </div>
            ) : filteredWanted.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '4px', color: 'var(--muted-2)', marginBottom: '8px' }}>NO WANTED CARDS FOUND</div>
                <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--border-2)' }}>Try adjusting your filters</div>
              </div>
            ) : (
              <div style={M.grid}>
                {filteredWanted.map(row => (
                  <WantedCard
                    key={row.card_key + row.user_id}
                    row={row}
                    profile={profileMap[row.user_id]}
                    photoOwnerId={wantedPhotoOwners[row.card_key]}
                    cardNameLookup={cardNameLookup}
                    isOwn={row.user_id === user?.id}
                    busy={contactBusy === row.card_key + row.user_id}
                    onContact={() => handleContactWanted(row)}
                  />
                ))}
              </div>
            )}
          </main>
        </>
      )}

      {view === 'sold' && (
        <>
          <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-2)', textAlign: 'center', padding: '12px 24px', letterSpacing: '0.5px', lineHeight: 1.6, borderBottom: '1px solid var(--panel-3)' }}>
            Completed sales on CollectionKeeper — real deals, CK's own price history. Buyers are never shown.
          </div>
          <main style={M.main}>
            {soldLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '4px', color: 'var(--muted-2)' }}>LOADING SOLD…</div>
              </div>
            ) : soldError ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '4px', color: 'var(--muted-2)', marginBottom: '8px' }}>COULDN'T LOAD SOLD FEED</div>
                <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--border-2)' }}>Try again in a moment.</div>
              </div>
            ) : soldRows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '4px', color: 'var(--muted-2)', marginBottom: '8px' }}>NO SALES YET</div>
                <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--border-2)' }}>Completed sales will show up here.</div>
              </div>
            ) : (
              <div style={M.grid}>
                {soldRows.map((row) => (
                  <SoldCard key={row.id} row={row} cardNameLookup={cardNameLookup} />
                ))}
              </div>
            )}
          </main>
        </>
      )}
    {modalItem && (
      <ListingDetailModal
        listing={toListingShape(modalItem, profileMap, cardNameLookup, statsMap)}
        onClose={() => setModalItem(null)}
        showToast={showToast}
        currentUserId={user?.id}
      />
    )}
    {toast && (
      <div style={M.toast} role="status" aria-live="polite">{toast}</div>
    )}
    </div>
  );
}

const M = {
  page: { minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Rajdhani',sans-serif" },
  stars: { position: 'fixed', inset: 0, opacity: 'var(--stars-opacity)', pointerEvents: 'none', zIndex: 0, backgroundImage: 'radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.35) 0%, transparent 100%), radial-gradient(1px 1px at 80% 60%, rgba(255,255,255,0.25) 0%, transparent 100%)' },
  screen: { minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  screenTxt: { fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '4px', color: 'var(--muted-2)' },
  header: { position: 'relative', zIndex: 10, background: 'rgba(var(--bg-rgb),0.97)', padding: '20px 24px 0', borderBottom: '1px solid var(--border)' },
  hInner: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', maxWidth: '1200px', margin: '0 auto', paddingBottom: '16px' },
  logoLine: { fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '4px', color: 'var(--brand-gold)', opacity: 0.85, marginBottom: '6px' },
  title: { fontFamily: "'Orbitron',monospace", fontSize: 'clamp(18px,3vw,28px)', fontWeight: 900, color: 'var(--text-strong)', margin: 0 },
  btn: { fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '1px', color: 'var(--muted-2)', background: 'transparent', border: '1px solid var(--border)', padding: '7px 12px', borderRadius: '2px', cursor: 'pointer', textDecoration: 'none' },
  statsBar: { display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', padding: '10px 20px', background: 'rgba(var(--bg-rgb),0.96)', borderBottom: '1px solid var(--border)', position: 'relative', zIndex: 10 },
  pill: { fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', padding: '4px 10px', borderRadius: '2px', border: '1px solid var(--border)' },
  filterBar: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '10px 24px', background: 'var(--bg-sticky)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 20 },
  filterLabel: { fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '2px', color: 'var(--muted-2)', flexShrink: 0 },
  select: { background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', padding: '6px 10px', borderRadius: '2px', cursor: 'pointer', outline: 'none' },
  main: { position: 'relative', zIndex: 1, padding: '20px', maxWidth: '1200px', margin: '0 auto' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: '12px' },
  toast: {
    position: 'fixed',
    bottom: 'max(20px, env(safe-area-inset-bottom))',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'var(--panel-2)',
    border: '0.5px solid rgba(var(--violet-rgb),0.4)',
    color: 'var(--text-bright)',
    padding: '10px 16px',
    borderRadius: 4,
    fontSize: 12,
    fontFamily: "'JetBrains Mono',monospace",
    boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
    zIndex: 3000,
    maxWidth: 'calc(100vw - 40px)',
    textAlign: 'center',
    lineHeight: 1.4,
  },
  card: { background: 'rgba(var(--pcard-rgb),0.85)', border: '1px solid var(--border)', borderRadius: '3px', padding: '14px', display: 'flex', flexDirection: 'column', transition: 'border-color 0.2s' },
  statusBadge: { fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '0.5px', padding: '3px 8px', border: '1px solid', borderRadius: '1px' },
  price: { fontFamily: "'Orbitron',monospace", fontSize: '14px', fontWeight: 700, color: 'var(--brand-gold)' },
  setName: { fontFamily: "'Orbitron',monospace", fontSize: '9px', letterSpacing: '1px', color: 'var(--muted-2)', marginBottom: '3px' },
  cardNum: { fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: '15px', color: 'var(--text-strong)', marginBottom: '8px' },
  divider: { height: '1px', background: 'var(--border)', margin: '10px 0' },
  seller: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--cyan)' },
  contactBtn: { fontFamily: "'Orbitron',monospace", fontSize: '8px', letterSpacing: '1px', color: 'var(--purple)', border: '1px solid rgba(var(--purple-rgb),0.4)', borderRadius: '2px', padding: '5px 10px', textDecoration: 'none', background: 'rgba(var(--purple-rgb),0.08)' },
};
